import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2 } from "lucide-react";
import EditableCell from "./EditableCell";
import {
  useGetAppsQuery,
  useCreateAppMutation,
  useUpdateAppMutation,
  useDeleteAppMutation,
  type App,
  type CreateAppParams,
} from "@/redux/api/apps.rtkq";

const AppsManagement = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<App | null>(null);
  const [formData, setFormData] = useState({
    appIdentifier: "",
    name: "",
    description: "",
    version: "",
    category: "",
  });

  // RTK Query hooks
  const { data: apps = [], isLoading, error } = useGetAppsQuery();
  const [createApp, { isLoading: isCreating }] = useCreateAppMutation();
  const [updateApp, { isLoading: isUpdating }] = useUpdateAppMutation();
  const [deleteApp, { isLoading: isDeleting }] = useDeleteAppMutation();

  const handleCreateApp = async () => {
    try {
      const appData: CreateAppParams = {
        appIdentifier: formData.appIdentifier,
        name: formData.name,
        description: formData.description,
        category: formData.category || undefined,
        isActive: true,
        version: formData.version || "1.0.0",
      };

      await createApp(appData).unwrap();
      setFormData({ appIdentifier: "", name: "", description: "", version: "", category: "" });
      setIsCreateDialogOpen(false);
    } catch (error) {
      console.error("Failed to create app:", error);
    }
  };

  const handleEditApp = async () => {
    if (!editingApp) return;

    try {
      const updates = {
        appIdentifier: formData.appIdentifier,
        name: formData.name,
        description: formData.description,
        category: formData.category || undefined,
        version: formData.version || editingApp.version,
      };

      await updateApp({ appId: editingApp.appId, updates }).unwrap();
      setEditingApp(null);
      setFormData({ appIdentifier: "", name: "", description: "", version: "", category: "" });
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error("Failed to update app:", error);
    }
  };

  const handleDeleteApp = async (appId: string) => {
    try {
      await deleteApp({ appId }).unwrap();
    } catch (error) {
      console.error("Failed to delete app:", error);
    }
  };


  const openEditDialog = (app: App) => {
    setEditingApp(app);
    setFormData({
      appIdentifier: app.appIdentifier,
      name: app.name,
      description: app.description,
      version: app.version || "",
      category: app.category || "",
    });
    setIsEditDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">טוען אפליקציות...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">שגיאה בטעינת האפליקציות</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">ניהול אפליקציות</h3>
          <p className="text-sm text-slate-600">הוסף, ערוך והסר אפליקציות מהמערכת</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-sky-400 to-indigo-500 text-white">
              <Plus className="h-4 w-4 ml-2" />
              אפליקציה חדשה
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>יצירת אפליקציה חדשה</DialogTitle>
              <DialogDescription>מלא את הפרטים לאפליקציה החדשה</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="appId">מזהה אפליקציה</Label>
                <Input
                  id="appId"
                  value={formData.appIdentifier}
                  onChange={(e) => setFormData({ ...formData, appIdentifier: e.target.value })}
                  placeholder="ai-assistant"
                />
              </div>
              <div>
                <Label htmlFor="name">שם האפליקציה</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="AI Personal Assistant"
                />
              </div>
              <div>
                <Label htmlFor="description">תיאור</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="תיאור קצר של האפליקציה"
                />
              </div>
              <div>
                <Label htmlFor="category">קטגוריה</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="AI Tools"
                />
              </div>
              <div>
                <Label htmlFor="version">גרסה</Label>
                <Input
                  id="version"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="1.0.0"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                ביטול
              </Button>
              <Button onClick={handleCreateApp} disabled={isCreating}>
                {isCreating ? "יוצר..." : "צור אפליקציה"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Apps Table */}
      <Card>
        <CardHeader>
          <CardTitle>אפליקציות במערכת</CardTitle>
          <CardDescription>רשימת כל האפליקציות הזמינות במערכת</CardDescription>
        </CardHeader>
        <CardContent>
          {apps.length === 0 ? (
            <div className="text-center py-8 text-slate-600">
              אין אפליקציות זמינות. צור אפליקציה ראשונה כדי להתחיל.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">מזהה</TableHead>
                  <TableHead className="text-right">שם</TableHead>
                  <TableHead className="text-right">תיאור</TableHead>
                  <TableHead className="text-right">קטגוריה</TableHead>
                  <TableHead className="text-right">גרסה</TableHead>
                  <TableHead className="text-right">סטטוס</TableHead>
                  <TableHead className="text-right">תאריך יצירה</TableHead>
                  <TableHead className="text-right">פעולות</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apps.map((app) => (
                  <TableRow key={app.appId}>
                    <TableCell className="font-mono text-sm text-right">{app.appId}</TableCell>
                    <TableCell className="text-right">
                      <EditableCell
                        value={app.name}
                        onSave={async (val) => { await updateApp({ appId: app.appId, updates: { name: val as string } }).unwrap(); }}
                        type="text"
                        displayValue={<span className="font-medium">{app.name}</span>}
                      />
                    </TableCell>
                    <TableCell className="text-right max-w-xs">
                      <EditableCell
                        value={app.description || ""}
                        onSave={async (val) => { await updateApp({ appId: app.appId, updates: { description: val as string } }).unwrap(); }}
                        type="text"
                        displayValue={<span className="truncate block">{app.description || "—"}</span>}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <EditableCell
                        value={app.category || ""}
                        onSave={async (val) => { await updateApp({ appId: app.appId, updates: { category: val as string } }).unwrap(); }}
                        type="text"
                        displayValue={app.category ? <Badge variant="outline">{app.category}</Badge> : <span className="text-slate-400">-</span>}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <EditableCell
                        value={app.version || ""}
                        onSave={async (val) => { await updateApp({ appId: app.appId, updates: { version: val as string } }).unwrap(); }}
                        type="text"
                        displayValue={<Badge variant="outline">{app.version}</Badge>}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <EditableCell
                        value={app.isActive}
                        onSave={async (val) => { await updateApp({ appId: app.appId, updates: { isActive: val as boolean } }).unwrap(); }}
                        type="toggle"
                        displayValue={
                          <Badge variant={app.isActive ? "default" : "secondary"}>
                            {app.isActive ? "פעיל" : "לא פעיל"}
                          </Badge>
                        }
                      />
                    </TableCell>
                    <TableCell className="text-right text-sm text-slate-600">
                      {app.createdAt ? new Date(app.createdAt).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(app)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteApp(app.appId)}
                          disabled={isDeleting}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>עריכת אפליקציה</DialogTitle>
            <DialogDescription>ערוך את פרטי האפליקציה</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-appId">מזהה אפליקציה</Label>
              <Input
                id="edit-appId"
                value={formData.appIdentifier}
                onChange={(e) => setFormData({ ...formData, appIdentifier: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-name">שם האפליקציה</Label>
              <Input id="edit-name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="edit-description">תיאור</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-category">קטגוריה</Label>
              <Input
                id="edit-category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-version">גרסה</Label>
              <Input
                id="edit-version"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              ביטול
            </Button>
            <Button onClick={handleEditApp} disabled={isUpdating}>
              {isUpdating ? "שומר..." : "שמור שינויים"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppsManagement;
